/**
 * ForecastCard — карточка прогноза
 *
 * Отображает результат прогноза: рейтинг, AI-интерпретацию,
 * рекомендации и предостережения.
 */

import React, { useState } from 'react';
import { renderMarkdown } from '../utils/renderMarkdown';
import { sanitizeHtml } from '../utils/sanitizeHtml';

interface ForecastCardProps {
  type: string;
  title: string;
  icon: string;
  rating?: number;
  tone?: string;
  summary: string;
  aiInterpretation?: string;
  recommendations?: string[];
  warnings?: string[];
  generatedAt?: string;
  cached?: boolean;
}

const ForecastCard: React.FC<ForecastCardProps> = ({
  type,
  title,
  icon,
  rating,
  tone,
  summary,
  aiInterpretation,
  recommendations,
  warnings,
  generatedAt,
  cached,
}) => {
  const [showDetails, setShowDetails] = useState(false);

  const toneColor =
    tone === 'positive'
      ? '#4CAF50'
      : tone === 'challenging'
        ? '#FF5252'
        : '#FFC107';

  const toneLabel =
    tone === 'positive'
      ? 'Благоприятный'
      : tone === 'challenging'
        ? 'Требует внимания'
        : tone
          ? 'Нейтральный'
          : '';

  // Звёзды рейтинга
  const stars = rating ? Math.round(rating) : 0;

  return (
    <div
      style={{
        background: 'rgba(13, 25, 48, 0.6)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(212, 175, 55, 0.15)',
        borderRadius: '20px',
        padding: '28px',
        marginBottom: '20px',
      }}
    >
      {/* Заголовок */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
        <div
          style={{
            width: '52px',
            height: '52px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.2) 0%, rgba(244, 208, 63, 0.1) 100%)',
            border: '1px solid rgba(212, 175, 55, 0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '26px',
            flexShrink: 0,
          }}
        >
          {icon}
        </div>

        <div style={{ flex: 1 }}>
          <h2
            style={{
              color: '#d4af37',
              fontSize: '20px',
              fontFamily: 'var(--font-display)',
              fontStyle: 'italic',
              margin: 0,
            }}
          >
            {title}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '4px' }}>
            {rating && (
              <div style={{ display: 'flex', gap: '2px' }}>
                {Array.from({ length: 5 }).map((_, i) => (
                  <span
                    key={i}
                    style={{
                      color: i < stars ? '#d4af37' : 'rgba(212, 175, 55, 0.2)',
                      fontSize: '14px',
                    }}
                  >
                    ★
                  </span>
                ))}
                <span style={{ color: '#b0b0b0', fontSize: '13px', marginLeft: '4px' }}>
                  {rating}/5
                </span>
              </div>
            )}
            {toneLabel && (
              <span
                style={{
                  display: 'inline-block',
                  padding: '2px 8px',
                  borderRadius: '8px',
                  background: `${toneColor}20`,
                  color: toneColor,
                  fontSize: '12px',
                  fontWeight: 'bold',
                }}
              >
                {toneLabel}
              </span>
            )}
            {cached && (
              <span
                style={{
                  display: 'inline-block',
                  padding: '2px 8px',
                  borderRadius: '8px',
                  background: 'rgba(33, 150, 243, 0.15)',
                  color: '#2196F3',
                  fontSize: '11px',
                }}
              >
                из кэша
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Summary */}
      <p
        style={{
          color: '#e0e0e0',
          fontSize: '15px',
          lineHeight: 1.6,
          margin: '0 0 16px',
          fontFamily: 'var(--font-sans)',
        }}
      >
        {summary}
      </p>

      {/* AI Interpretation — раскрываемый блок */}
      {aiInterpretation && (
        <div style={{ marginBottom: '16px' }}>
          <button
            onClick={() => setShowDetails(!showDetails)}
            style={{
              background: 'rgba(212, 175, 55, 0.1)',
              border: '1px solid rgba(212, 175, 55, 0.2)',
              borderRadius: '8px',
              padding: '10px 16px',
              color: '#d4af37',
              fontSize: '14px',
              cursor: 'pointer',
              width: '100%',
              textAlign: 'left',
              fontFamily: 'var(--font-sans)',
              transition: 'all 0.2s ease',
            }}
          >
            {showDetails ? 'Скрыть подробности' : 'Показать интерпретацию'}{' '}
            <span style={{ float: 'right' }}>{showDetails ? '▲' : '▼'}</span>
          </button>

          {showDetails && (
            <div
              style={{
                marginTop: '12px',
                padding: '20px',
                background: 'rgba(13, 25, 48, 0.4)',
                borderRadius: '12px',
                border: '1px solid rgba(212, 175, 55, 0.1)',
                color: '#e0e0e0',
                fontSize: '15px',
                lineHeight: 1.8,
                fontFamily: 'var(--font-sans)',
              }}
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(renderMarkdown(aiInterpretation)),
              }}
            />
          )}
        </div>
      )}

      {/* Recommendations */}
      {recommendations && recommendations.length > 0 && (
        <div style={{ marginBottom: '12px' }}>
          <h4
            style={{
              color: '#4CAF50',
              fontSize: '14px',
              margin: '0 0 8px',
              fontFamily: 'var(--font-sans)',
            }}
          >
            Рекомендации
          </h4>
          <ul style={{ margin: 0, paddingLeft: '20px', color: '#b0b0b0', fontSize: '14px', lineHeight: 1.6 }}>
            {recommendations.map((rec, i) => (
              <li key={i}>{rec}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Warnings */}
      {warnings && warnings.length > 0 && (
        <div>
          <h4
            style={{
              color: '#FFC107',
              fontSize: '14px',
              margin: '0 0 8px',
              fontFamily: 'var(--font-sans)',
            }}
          >
            Предостережения
          </h4>
          <ul style={{ margin: 0, paddingLeft: '20px', color: '#b0b0b0', fontSize: '14px', lineHeight: 1.6 }}>
            {warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Footer */}
      {generatedAt && (
        <p
          style={{
            color: '#666',
            fontSize: '12px',
            margin: '16px 0 0',
            textAlign: 'right',
            fontFamily: 'var(--font-sans)',
          }}
        >
          Дата расчёта: {new Date(generatedAt).toLocaleString('ru-RU')}
        </p>
      )}
    </div>
  );
};

export default ForecastCard;
